import { Button, Popover, Stack, Typography } from '@mui/material';
import { forwardRef, useMemo, useState, type UIEventHandler } from 'react';

import { SelectionPanel } from '@/components/panels/selectionPanel/selectionPanel';
import { ScrollableContent } from '@/components/scroll/scrollableContent';
import { ScrollableWrapper } from '@/components/scroll/scrollableContext';
import { ScrollableHeader } from '@/components/scroll/scrollableHeader';
import { useScrollable } from '@/components/scroll/useScrollable';
import {
  conditionValues,
  type FilterOption,
  type MultiSelectConditionPanelProps,
} from '@/types/advanceFilter';
import { pluralize } from '@/utils/string';

import { FilterOperatorSelect } from './filterOperatorSelect';
import { useConditionPanel } from './useConditionPanel';

import { AddValuesPanel } from '../panels/addValuesPanel';
import { MultiSelectFilter } from '../queryBuilder/filters';
import { SearchBar } from '../search/searchBar';
import { TextDetailsTuple } from '../text/textDetailsTuple';

/**
 * What the panel renders, minus the host wiring the panel resolves on its own. `showOperatorSelect` is resolved the
 * same way: hosted panels own their operator picker, while an inline condition row already has one of its own.
 */
type PanelContentProps = Omit<
  MultiSelectConditionPanelProps,
  'panelHost' | 'onPanelClose'
> & { showOperatorSelect: boolean };

/** One search row of the popover's fixed height, which the option list gets back when no box renders. */
const SEARCH_ROW_HEIGHT = 44;

const optionListHeight = ({
  hasSelection,
  hasSearchBox,
}: {
  hasSelection: boolean;
  hasSearchBox: boolean;
}) => {
  const withSearchBox = hasSelection ? 270 : 330;
  return `${hasSearchBox ? withSearchBox : withSearchBox + SEARCH_ROW_HEIGHT}px`;
};

const ValuesFilter = (
  props: PanelContentProps & {
    showNameFirst: boolean;
    shouldShowId: boolean;
    showSelectAll: boolean;
  }
) => {
  const [valueFilter, setValueFilter] = useState('');

  const selectedValues = useMemo(
    () => conditionValues(props.condition),
    [props.condition]
  );

  const serverSideSearch = !!props.onSearchChange;

  const handleSearchChange = (value: string) => {
    setValueFilter(value);
    props.onSearchChange?.(value);
  };

  const values = useMemo(
    () =>
      props.options
        .filter(
          (option) =>
            serverSideSearch ||
            !valueFilter ||
            option.id.toLowerCase().includes(valueFilter.toLowerCase()) ||
            option.name?.toLowerCase().includes(valueFilter.toLowerCase())
        )
        .sort(),
    [props.options, valueFilter, serverSideSearch]
  );

  const nothingToSearch =
    !props.isLoading && !props.options.length && !serverSideSearch;

  if (nothingToSearch && props.showAddNewButton) {
    return (
      <AddValuesPanel
        entityName={props.entityName}
        addedValues={selectedValues}
        onAddClick={(value) => {
          props.onChange({ ...props.condition, value });
        }}
        renderItem={(item) => (
          <SelectionItem
            item={item}
            shouldShowId={props.shouldShowId}
            showNameFirst={props.showNameFirst}
            options={props.options}
          />
        )}
        itemKey={(item) => item}
        clearAll={() => props.onChange({ ...props.condition, value: [] })}
        onRemove={(item) => {
          const values = selectedValues.filter((x) => x !== item);
          props.onChange({ ...props.condition, value: values });
        }}
      />
    );
  }

  return (
    <SelectionPanel
      isLoading={serverSideSearch ? false : props.isLoading}
      drawerProps={{
        items: selectedValues,
        entityType: props.entityName,
        onRemove: (item) => {
          const values = selectedValues.filter((x) => x !== item);
          props.onChange({ ...props.condition, value: values });
        },
        renderItem: (item) => (
          <SelectionItem
            item={item}
            shouldShowId={props.shouldShowId}
            showNameFirst={props.showNameFirst}
            options={props.options}
          />
        ),
        itemKey: (item) => item,
        clearAll: () => props.onChange({ ...props.condition, value: [] }),
      }}
    >
      <ScrollableWrapper>
        <ValuesFilterBody
          {...props}
          values={values}
          selectedValues={selectedValues}
          valueFilter={valueFilter}
          handleSearchChange={handleSearchChange}
        />
      </ScrollableWrapper>
    </SelectionPanel>
  );
};

const ValuesFilterBody = (
  props: Parameters<typeof ValuesFilter>[0] & {
    values: FilterOption[];
    selectedValues: string[];
    valueFilter: string;
    handleSearchChange: (value: string) => void;
  }
) => {
  const { isScrolledVertically, setIsScrolledVertically } = useScrollable();

  const onScroll: UIEventHandler<HTMLDivElement> = (event) => {
    const target = event.target as HTMLDivElement;
    if (typeof target.scrollTop === 'number') {
      const isScrolled = target.scrollTop > 0;
      if (isScrolled !== isScrolledVertically) {
        setIsScrolledVertically(isScrolled);
      }
    }
  };

  return (
    <>
      <ScrollableHeader hideBorderWhenOnTop className='px-[24px] py-[12px]'>
        {props.showOperatorSelect ? (
          <Stack sx={{ marginY: '12px' }}>
            <FilterOperatorSelect
              condition={props.condition}
              property={props.property}
              onChange={props.onChange}
            />
          </Stack>
        ) : (
          <Typography className='my-[12px]' variant='body1'>
            {`Select one or more ${pluralize(props.entityName)}`}
          </Typography>
        )}
        {props.showSearchBox && (
          <SearchBar
            value={props.valueFilter}
            setValue={props.handleSearchChange}
            placeholder={`Search...`}
            selectedValues={props.selectedValues}
            onAddClick={(value) => {
              props.onChange({ ...props.condition, value });
            }}
            showAddButton={props.showAddNewButton}
          />
        )}
      </ScrollableHeader>
      <ScrollableContent className='px-[24px] py-[12px]'>
        {props.values.length === 0 && (
          <Typography variant='body1' className='my-[12px]'>
            {props.showAddNewButton
              ? `No matching values found. Try a new search term, or add this value manually to include it once it’s discovered.`
              : `No matching values found. Try a new search term.`}
          </Typography>
        )}
        {props.values.length > 0 && (
          <MultiSelectFilter
            property={props.property}
            data-testid={`filter-${props.property.name}`}
            conditions={[props.condition]}
            onChange={(conditions) =>
              conditions.length
                ? props.onChange(conditions[0])
                : props.onChange({ ...props.condition, value: [] })
            }
            options={props.values}
            onVirtuosoScroll={onScroll}
            virtuosoStyles={{
              height: optionListHeight({
                hasSelection: !!props.selectedValues.length,
                hasSearchBox: props.showSearchBox,
              }),
              marginRight: '-24px',
              paddingRight: '24px',
            }}
            showNameFirst={props.showNameFirst}
            shouldShowId={props.shouldShowId}
            showSelectAll={props.showSelectAll}
          />
        )}
      </ScrollableContent>
    </>
  );
};

const SelectionItem = ({
  item,
  shouldShowId,
  showNameFirst,
  options,
}: {
  item: string;
  shouldShowId: boolean;
  showNameFirst: boolean;
  options: FilterOption[];
}) => {
  const option = options.find((x) => x.id === item);

  if (option?.Image && option.hideName) {
    return (
      <Stack
        direction='row'
        sx={{
          overflow: 'hidden',
          alignItems: 'center',
        }}
      >
        {option.Image}
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        gap: '4px',
        overflow: 'hidden',
      }}
    >
      <Stack
        direction='row'
        sx={{
          overflow: 'hidden',
          alignItems: 'center',
          gap: '12px',
        }}
      >
        {option?.Image}
        <TextDetailsTuple
          primaryProp={
            shouldShowId
              ? showNameFirst
                ? option?.name
                : option?.id
              : // Fall back to the raw value for options that aren't in the list (manually added, not discovered yet).
                option?.name || item
          }
          secondaryProp={
            shouldShowId
              ? showNameFirst
                ? option?.id
                : option?.name
              : undefined
          }
        />
      </Stack>
      {!option && (
        <Typography
          sx={{
            color: 'var(--mui-palette-info-main)',
          }}
        >
          Not discovered yet
        </Typography>
      )}
    </Stack>
  );
};

export const ConditionPanelTriggerButton = forwardRef<
  HTMLButtonElement,
  {
    selectedValues: string[];
    options: FilterOption[];
    onClick: () => void;
    useImagesInSelectedView?: boolean;
    shouldShowId?: boolean;
  }
>(
  (
    { selectedValues, onClick, options, useImagesInSelectedView, shouldShowId },
    ref
  ) => {
    return (
      <Button
        ref={ref}
        onClick={onClick}
        disableFocusRipple
        size='small'
        className='px-0 py-[5px] min-h-[32px] h-fit-content'
      >
        <Stack
          direction={'row-reverse'}
          className='pl-[12px] pr-[8px]'
          sx={{
            height: 'fit-content',
            alignItems: 'start',
            gap: '8px',
          }}
        >
          <i className='material-symbols-arrow-drop-down-rounded text-xl align-middle min-w-[22px] min-h-[22px] w-[22px] h-[22px]' />
          {selectedValues.length === 0 && (
            <Typography
              variant='button'
              className='text-left font-medium'
              sx={{
                color: 'var(--mui-palette-text-disabled)',
              }}
            >
              Select
            </Typography>
          )}
          {!useImagesInSelectedView && selectedValues.length > 0 && (
            <Typography
              variant='button'
              className='text-left font-medium'
              sx={{ color: 'currentcolor' }}
            >
              {selectedValues
                .map((x) => {
                  const item = options.find((y) => y.id === x);
                  return item ? (shouldShowId ? item?.id : item?.name) : x;
                })
                .join(', ')}
            </Typography>
          )}
          {useImagesInSelectedView && selectedValues.length > 0 && (
            <Stack
              direction='row'
              sx={{
                flexWrap: 'wrap',
                columnGap: '8px',
                rowGap: '4px',
                '& > *': { flexShrink: 0 },
              }}
            >
              {selectedValues.map(
                (x) => options.find((y) => y.id === x)?.Image || x
              )}
            </Stack>
          )}
        </Stack>
      </Button>
    );
  }
);

export const MultiSelectConditionPanel = (
  props: MultiSelectConditionPanelProps
) => {
  const panel = useConditionPanel(props.panelHost, props.onPanelClose);

  const selectedValues = conditionValues(props.condition);

  return (
    <>
      {!panel.isHosted && (
        <ConditionPanelTriggerButton
          ref={panel.triggerRef}
          onClick={panel.open}
          useImagesInSelectedView={props.useImagesInSelectedView}
          shouldShowId={props.shouldShowId}
          options={props.options}
          selectedValues={selectedValues}
        />
      )}
      <ValuesConditionPanelPopover
        {...props}
        showAddNewButton={!panel.isHosted && props.showAddNewButton}
        // Select-all belongs to the full panel: it rewrites the condition with the property's default operator,
        // which in a chip panel would silently undo the operator shown right above the list.
        showSelectAll={!panel.isHosted && props.showSelectAll}
        showOperatorSelect={panel.isHosted}
        anchorEl={panel.anchorEl}
        popoverOpen={panel.isOpen}
        onClose={panel.close}
      />
    </>
  );
};

const ValuesConditionPanelPopover = (
  props: PanelContentProps & {
    onClose: () => void;
    anchorEl: HTMLElement | null;
    popoverOpen: boolean;
  }
) => {
  return (
    <Popover
      open={props.popoverOpen}
      anchorEl={props.anchorEl}
      onClose={props.onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      slotProps={{ paper: { sx: { marginTop: '4px' } } }}
    >
      <Stack className='w-[308px]'>
        <ValuesFilter
          {...props}
          condition={{ ...props.condition }}
          showNameFirst={!!props.showNameFirst}
          shouldShowId={!!props.shouldShowId}
          showSelectAll={!!props.showSelectAll}
        />
      </Stack>
    </Popover>
  );
};
